import styled from 'styled-components';
import tw from 'twin.macro';

const Container = styled.div`
  ${tw`mt-4`}
`;

const Title = styled.p`
  ${tw`text-sm text-text-secondary mb-3`}
`;

const TagsContainer = styled.div`
  ${tw`flex flex-wrap gap-2`}
`;

const Tag = styled.button<{ $isSelected: boolean }>`
  ${tw`px-3 py-2 rounded-full text-sm font-medium transition-all duration-150 cursor-pointer border`}
  ${({ $isSelected }) =>
    $isSelected
      ? tw`bg-black text-white border-black`
      : tw`bg-white text-text-secondary border-border-default hover:border-purple-400`}
`;

// Type for improvement labels
type ImprovementLabels = Record<string, string>;

// Improvement tag labels for each category
export const MAINTENANCE_IMPROVEMENTS: ImprovementLabels = {
  WINDSHIELD_WIPERS: 'Windshield wipers',
  LOW_TIRE_PRESSURE: 'Low tire pressure',
  BRAKES: 'Brakes',
  MIRRORS: 'Mirrors',
  LOW_CAR_BATTERY: 'Low car battery',
  WINDSHIELD: 'Windshield',
  TIRE_THREADS: 'Tire threads',
  HEADLIGHTS: 'Headlights',
  TAILLIGHTS: 'Taillights',
  CHECK_ENGINE_LIGHT: 'Check engine light',
  LOW_OIL: 'Low oil',
  OTHERS: 'Others',
};

export const CLEANLINESS_IMPROVEMENTS: ImprovementLabels = {
  INTERIOR_DIRTY: 'Interior dirty',
  EXTERIOR_DIRTY: 'Exterior dirty',
  NOT_DISINFECTED: 'Not disinfected',
  UNPLEASANT_SMELL: 'Unpleasant smell',
  UPHOLSTERY_STAINS: 'Upholstery stains',
  PET_HAIR: 'Pet hair',
  TRASH_OR_CLUTTER: 'Trash or clutter',
  OTHERS: 'Others',
};

export const COMMUNICATION_IMPROVEMENTS: ImprovementLabels = {
  NOT_RESPONSIVE: 'Not responsive',
  SLOW_RESPONSE: 'Slow response',
  UNCLEAR_INSTRUCTIONS: 'Unclear instructions',
  RUDE_BEHAVIOR: 'Rude behavior',
  OTHERS: 'Others',
};

export const CONVENIENCE_IMPROVEMENTS: ImprovementLabels = {
  CAR_LOCATION_ISSUE: 'Car location issue',
  KEY_LOCATION_ISSUE: 'Key location issue',
  DIFFICULT_PICKUP: 'Difficult pickup',
  DIFFICULT_DROPOFF: 'Difficult drop-off',
  OTHERS: 'Others',
};

export const ACCURACY_IMPROVEMENTS: ImprovementLabels = {
  PHOTOS_INACCURATE: 'Photos inaccurate',
  FEATURES_MISSING: 'Features missing',
  SHOWED_UP_LATE: 'Showed up late',
  DIFFERENT_CAR: 'Different car',
  OTHERS: 'Others',
};

interface ImprovementSelectorProps {
  category: 'maintenance' | 'cleanliness' | 'communication' | 'convenience' | 'accuracy';
  selectedImprovements: string[];
  onImprovementsChange: (improvements: string[]) => void;
}

export const ImprovementSelector = ({
  category,
  selectedImprovements,
  onImprovementsChange,
}: ImprovementSelectorProps) => {
  const getImprovements = (): ImprovementLabels => {
    switch (category) {
      case 'maintenance':
        return MAINTENANCE_IMPROVEMENTS;
      case 'cleanliness':
        return CLEANLINESS_IMPROVEMENTS;
      case 'communication':
        return COMMUNICATION_IMPROVEMENTS;
      case 'convenience':
        return CONVENIENCE_IMPROVEMENTS;
      case 'accuracy':
        return ACCURACY_IMPROVEMENTS;
      default:
        return {};
    }
  };

  const improvements = getImprovements();

  const toggleImprovement = (key: string) => {
    if (selectedImprovements.includes(key)) {
      onImprovementsChange(selectedImprovements.filter(i => i !== key));
    } else {
      onImprovementsChange([...selectedImprovements, key]);
    }
  };

  return (
    <Container>
      <Title>What could be improved? (Optional)</Title>
      <TagsContainer>
        {Object.entries(improvements).map(([key, label]) => (
          <Tag
            key={key}
            type="button"
            $isSelected={selectedImprovements.includes(key)}
            onClick={() => toggleImprovement(key)}
          >
            {label}
          </Tag>
        ))}
      </TagsContainer>
    </Container>
  );
};
